import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Checkbox,
  Divider,
  List,
  ListItem,
  FormControlLabel,
  TextField,
  Button,
  Box
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import {
  formatCurrency,
  formatDate,
  calcularTotalComisiones,
  calcularTotalEgresos
} from '../../utils/liquidacionHelpers';

/**
 * Componente de diálogo para generar liquidación
 * @param {boolean} open - Si el diálogo está abierto
 * @param {Function} onClose - Función para cerrar el diálogo
 * @param {Object} trabajador - Datos del trabajador
 * @param {Array} serviciosDetalle - Lista de servicios
 * @param {Array} egresosDetalle - Lista de egresos
 * @param {Object} liquidacionForm - Formulario de liquidación
 * @param {Function} setLiquidacionForm - Setter del formulario
 * @param {Function} onGenerar - Callback al generar liquidación
 * @param {boolean} loading - Estado de carga
 */
const DialogGenerarLiquidacion = ({
  open,
  onClose,
  trabajador,
  serviciosDetalle,
  egresosDetalle,
  liquidacionForm,
  setLiquidacionForm,
  onGenerar,
  loading
}) => {
  const totalComisiones = calcularTotalComisiones(
    serviciosDetalle,
    liquidacionForm.servicios_seleccionados
  );
  const totalEgresos = calcularTotalEgresos(
    egresosDetalle,
    liquidacionForm.egresos_seleccionados
  );
  const totalNeto = totalComisiones - totalEgresos;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          💰 Generar Liquidación - {trabajador?.nombre_trabajador}{' '}
          {trabajador?.apellido_trabajador}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* Servicios a liquidar */}
          <Grid item xs={12} md={8}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: '#4CAF50', fontWeight: 'bold' }}
            >
              ✅ Servicios Pendientes ({serviciosDetalle.length})
            </Typography>
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={
                          liquidacionForm.servicios_seleccionados.length ===
                            serviciosDetalle.length && serviciosDetalle.length > 0
                        }
                        indeterminate={
                          liquidacionForm.servicios_seleccionados.length > 0 &&
                          liquidacionForm.servicios_seleccionados.length <
                            serviciosDetalle.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setLiquidacionForm({
                              ...liquidacionForm,
                              servicios_seleccionados: serviciosDetalle.map(
                                (s) => s.id_asignacion
                              )
                            });
                          } else {
                            setLiquidacionForm({
                              ...liquidacionForm,
                              servicios_seleccionados: []
                            });
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell>Orden</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Servicio</TableCell>
                    <TableCell align="right">Precio</TableCell>
                    <TableCell align="right">Comisión</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {serviciosDetalle.map((servicio) => (
                    <TableRow key={servicio.id_asignacion}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={liquidacionForm.servicios_seleccionados.includes(
                            servicio.id_asignacion
                          )}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setLiquidacionForm({
                                ...liquidacionForm,
                                servicios_seleccionados: [
                                  ...liquidacionForm.servicios_seleccionados,
                                  servicio.id_asignacion
                                ]
                              });
                            } else {
                              setLiquidacionForm({
                                ...liquidacionForm,
                                servicios_seleccionados:
                                  liquidacionForm.servicios_seleccionados.filter(
                                    (id) => id !== servicio.id_asignacion
                                  )
                              });
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell>#{servicio.id_orden_servicio}</TableCell>
                      <TableCell>
                        {servicio.nombre_cliente} {servicio.apellido_cliente}
                      </TableCell>
                      <TableCell>{servicio.nombre_servicio}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(servicio.precio_servicio)}
                      </TableCell>
                      <TableCell align="right">{servicio.comision}%</TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 'bold', color: '#4CAF50' }}
                      >
                        {formatCurrency(servicio.comision_total)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          {/* Egresos a descontar */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: '#FF5722', fontWeight: 'bold' }}
            >
              💸 Egresos Pendientes ({egresosDetalle.length})
            </Typography>
            <List
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                bgcolor: 'rgba(255, 87, 34, 0.05)',
                borderRadius: 2
              }}
            >
              {egresosDetalle.map((egreso) => (
                <ListItem key={egreso.id_egreso} sx={{ px: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={liquidacionForm.egresos_seleccionados.includes(
                          egreso.id_egreso
                        )}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setLiquidacionForm({
                              ...liquidacionForm,
                              egresos_seleccionados: [
                                ...liquidacionForm.egresos_seleccionados,
                                egreso.id_egreso
                              ]
                            });
                          } else {
                            setLiquidacionForm({
                              ...liquidacionForm,
                              egresos_seleccionados:
                                liquidacionForm.egresos_seleccionados.filter(
                                  (id) => id !== egreso.id_egreso
                                )
                            });
                          }
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {egreso.tipo_egreso}: {formatCurrency(egreso.monto)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {egreso.concepto}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {formatDate(egreso.fecha_egreso)}
                        </Typography>
                      </Box>
                    }
                    sx={{ margin: 0, width: '100%' }}
                  />
                </ListItem>
              ))}
            </List>

            {egresosDetalle.length === 0 && (
              <Box
                sx={{
                  p: 2,
                  textAlign: 'center',
                  bgcolor: 'rgba(76, 175, 80, 0.05)',
                  borderRadius: 2
                }}
              >
                <CheckIcon sx={{ color: '#4CAF50', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  Sin egresos pendientes
                </Typography>
              </Box>
            )}
          </Grid>

          {/* Resumen de liquidación */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Paper sx={{ p: 3, bgcolor: 'rgba(26, 35, 126, 0.05)' }}>
              <Typography
                variant="h6"
                gutterBottom
                sx={{ color: '#1A237E', fontWeight: 'bold' }}
              >
                📊 Resumen de Liquidación
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Servicios seleccionados:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {liquidacionForm.servicios_seleccionados.length}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Comisiones brutas:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>
                    {formatCurrency(totalComisiones)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Descuentos:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#FF5722' }}>
                    -{formatCurrency(totalEgresos)}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total neto a pagar:
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 'bold',
                      color: '#1A237E',
                      fontSize: '1.5rem'
                    }}
                  >
                    {formatCurrency(totalNeto)}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* Observaciones */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Observaciones (opcional)"
              multiline
              rows={3}
              value={liquidacionForm.observaciones}
              onChange={(e) =>
                setLiquidacionForm({
                  ...liquidacionForm,
                  observaciones: e.target.value
                })
              }
              placeholder="Ej: Liquidación correspondiente al período..."
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={onGenerar}
          disabled={loading || liquidacionForm.servicios_seleccionados.length === 0}
          sx={{
            background: 'linear-gradient(135deg, #4CAF50 0%, #45A049 100%)'
          }}
        >
          {loading ? 'Generando...' : 'Generar Liquidación'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogGenerarLiquidacion;
